import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronDownIcon } from 'lucide-react';
import type { NavSubItem } from '../../data/navigation';
import { NavIconGlyph } from './NavIconGlyph';
type NavDropdownProps = {
  label: string;
  items: NavSubItem[];
};
export function NavDropdown({
  label,
  items
}: NavDropdownProps) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<number>();
  const wide = items.length > 4;
  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    const handleClick = (e: MouseEvent) => {
      if (!wrapperRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('keydown', handleKey);
    document.addEventListener('mousedown', handleClick);
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.removeEventListener('mousedown', handleClick);
    };
  }, [open]);
  const show = () => {
    window.clearTimeout(closeTimer.current);
    setOpen(true);
  };
  const hide = () => {
    closeTimer.current = window.setTimeout(() => setOpen(false), 120);
  };
  return <div ref={wrapperRef} className="relative" onMouseEnter={show} onMouseLeave={hide} data-id="element-535" data-mp="1skegcw0jk4ye">
      <button type="button" aria-expanded={open} aria-haspopup="true" onClick={() => setOpen(v => !v)} className={`flex items-center gap-1 rounded-sm text-sm font-medium transition-colors duration-150 ease-out hover:text-brand-red focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-red ${open ? 'text-brand-red' : 'text-gray-600'}`} data-id="element-536" data-mp="1ok4d3r44vsn8">
        {label}
        <ChevronDownIcon className={`h-4 w-4 transition-transform duration-200 ease-out ${open ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-537" data-mp="10sywvaua6yce" />
      </button>

      <AnimatePresence data-id="element-538" data-mp="vvysd4d2y3ne">
        {open && <motion.div initial={{
        opacity: 0,
        y: -4,
        scale: 0.98
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: -4,
        scale: 0.98
      }} transition={{
        duration: 0.16,
        ease: [0.23, 1, 0.32, 1]
      }} className={`absolute left-1/2 top-full z-50 -translate-x-1/2 pt-4 ${wide ? 'w-[620px]' : 'w-[320px]'}`} data-id="element-539" data-mp="xaroo3z95lry">
            <div className="rounded-xl border border-gray-100 bg-white p-3 shadow-xl" data-id="element-540" data-mp="ektgzozd9uou">
              <ul className={`grid gap-1 ${wide ? 'grid-cols-2' : 'grid-cols-1'}`} data-id="element-541" data-mp="jb9vzdiu5odb">
                {items.map(item => <li key={item.name} data-id="element-542" data-mp="4n5ak1seeyqu">
                    <a href={item.href} onClick={() => setOpen(false)} className="group flex items-start gap-3 rounded-lg p-3 transition-colors duration-150 ease-out hover:bg-brand-tint focus:outline-none focus-visible:bg-brand-tint" data-id="element-543" data-mp="4o6wx2ev77nv">
                      <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-md bg-brand-light text-brand-black transition-colors duration-150 ease-out group-hover:bg-brand-red group-hover:text-white" data-id="element-544" data-mp="mlgx871ad9g">
                        <NavIconGlyph icon={item.icon} className="h-[18px] w-[18px]" data-id="element-545" data-mp="3m5ko1k2sp59u" />
                      </span>
                      <span data-id="element-546" data-mp="3i2eikenibi4f">
                        <span className="block text-sm font-semibold text-brand-black" data-id="element-547" data-mp="3i266uq34eiut">{item.name}</span>
                        <span className="mt-0.5 block text-xs text-brand-gray" data-id="element-548" data-mp="3w15txtazipov">{item.description}</span>
                      </span>
                    </a>
                  </li>)}
              </ul>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}